import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { type HTMLAttributes, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface PaginationProps extends Omit<HTMLAttributes<HTMLElement>, 'onChange'> {
  /**
   * Current page (1-indexed)
   */
  currentPage: number;

  /**
   * Total number of pages
   */
  totalPages: number;

  /**
   * Callback when page changes
   */
  onPageChange: (page: number) => void;

  /**
   * Number of page buttons to show around current page
   * @default 1
   */
  siblingCount?: number;

  /**
   * Show first/last page buttons
   * @default true
   */
  showFirstLast?: boolean;

  /**
   * Show previous/next buttons
   * @default true
   */
  showPrevNext?: boolean;

  /**
   * Size variant
   * @default 'md'
   */
  size?: 'sm' | 'md' | 'lg';

  /**
   * Disabled state
   * @default false
   */
  disabled?: boolean;
}

/**
 * Pagination component
 *
 * Pagination controls for navigating through pages of data.
 *
 * @example
 * ```tsx
 * <Pagination
 *   currentPage={1}
 *   totalPages={10}
 *   onPageChange={(page) => console.log(page)}
 * />
 * ```
 */
export const Pagination = forwardRef<HTMLElement, PaginationProps>(
  (
    {
      currentPage,
      totalPages,
      onPageChange,
      siblingCount = 1,
      showFirstLast = true,
      showPrevNext = true,
      size = 'md',
      disabled = false,
      className,
      ...props
    },
    ref
  ) => {
    const sizeStyles = {
      sm: 'text-xs',
      md: 'text-sm',
      lg: 'text-base',
    };

    const buttonSizeStyles = {
      sm: 'h-7 min-w-[28px] px-2',
      md: 'h-9 min-w-[36px] px-3',
      lg: 'h-11 min-w-[44px] px-4',
    };

    // Generate page numbers to display
    const getPageNumbers = (): (number | string)[] => {
      const range = (start: number, end: number) => {
        const length = end - start + 1;
        return Array.from({ length }, (_, i) => start + i);
      };

      const totalNumbers = siblingCount * 2 + 3; // siblings + current + first + last
      const totalBlocks = totalNumbers + 2; // + 2 ellipsis

      if (totalPages <= totalBlocks) {
        return range(1, totalPages);
      }

      const leftSiblingIndex = Math.max(currentPage - siblingCount, 1);
      const rightSiblingIndex = Math.min(currentPage + siblingCount, totalPages);

      const shouldShowLeftDots = leftSiblingIndex > 2;
      const shouldShowRightDots = rightSiblingIndex < totalPages - 1;

      if (!shouldShowLeftDots && shouldShowRightDots) {
        const leftItemCount = 3 + 2 * siblingCount;
        const leftRange = range(1, leftItemCount);
        return [...leftRange, '...', totalPages];
      }

      if (shouldShowLeftDots && !shouldShowRightDots) {
        const rightItemCount = 3 + 2 * siblingCount;
        const rightRange = range(totalPages - rightItemCount + 1, totalPages);
        return [1, '...', ...rightRange];
      }

      if (shouldShowLeftDots && shouldShowRightDots) {
        const middleRange = range(leftSiblingIndex, rightSiblingIndex);
        return [1, '...', ...middleRange, '...', totalPages];
      }

      return [];
    };

    const pages = getPageNumbers();

    const handlePageChange = (page: number) => {
      if (disabled || page < 1 || page > totalPages || page === currentPage) {
        return;
      }
      onPageChange(page);
    };

    const buttonBaseStyles = [
      'inline-flex items-center justify-center',
      'rounded-md font-medium',
      'transition-colors duration-200',
      'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
      'disabled:opacity-50 disabled:cursor-not-allowed',
    ];

    const pageButtonStyles = (isActive: boolean) =>
      cn(
        buttonBaseStyles,
        buttonSizeStyles[size],
        isActive
          ? 'bg-primary-600 text-white hover:bg-primary-700'
          : 'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
      );

    const navButtonStyles = cn(
      buttonBaseStyles,
      buttonSizeStyles[size],
      'text-gray-700 hover:bg-gray-100 active:bg-gray-200'
    );

    return (
      <nav
        ref={ref}
        className={cn('flex items-center gap-1', sizeStyles[size], className)}
        aria-label="Pagination"
        {...props}
      >
        {showFirstLast && (
          <button
            type="button"
            onClick={() => handlePageChange(1)}
            disabled={disabled || currentPage === 1}
            className={navButtonStyles}
            aria-label="Go to first page"
          >
            First
          </button>
        )}

        {showPrevNext && (
          <button
            type="button"
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={disabled || currentPage === 1}
            className={navButtonStyles}
            aria-label="Go to previous page"
          >
            <ChevronLeftIcon className="h-4 w-4" />
          </button>
        )}

        {pages.map((page, index) => {
          if (page === '...') {
            return (
              <span
                key={`ellipsis-${index}`}
                className={cn('inline-flex items-center justify-center', buttonSizeStyles[size])}
              >
                ...
              </span>
            );
          }

          const pageNumber = page as number;
          const isActive = pageNumber === currentPage;

          return (
            <button
              key={pageNumber}
              type="button"
              onClick={() => handlePageChange(pageNumber)}
              disabled={disabled}
              className={pageButtonStyles(isActive)}
              aria-label={`Go to page ${pageNumber}`}
              aria-current={isActive ? 'page' : undefined}
            >
              {pageNumber}
            </button>
          );
        })}

        {showPrevNext && (
          <button
            type="button"
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={disabled || currentPage === totalPages}
            className={navButtonStyles}
            aria-label="Go to next page"
          >
            <ChevronRightIcon className="h-4 w-4" />
          </button>
        )}

        {showFirstLast && (
          <button
            type="button"
            onClick={() => handlePageChange(totalPages)}
            disabled={disabled || currentPage === totalPages}
            className={navButtonStyles}
            aria-label="Go to last page"
          >
            Last
          </button>
        )}
      </nav>
    );
  }
);

Pagination.displayName = 'Pagination';

export interface PaginationInfoProps extends HTMLAttributes<HTMLParagraphElement> {
  /**
   * Current page (1-indexed)
   */
  currentPage: number;

  /**
   * Items per page
   */
  pageSize: number;

  /**
   * Total number of items
   */
  totalItems: number;
}

/**
 * PaginationInfo component
 *
 * Displays pagination information (e.g., "Showing 1 to 10 of 100 results").
 */
export const PaginationInfo = forwardRef<HTMLParagraphElement, PaginationInfoProps>(
  ({ currentPage, pageSize, totalItems, className, ...props }, ref) => {
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalItems);

    return (
      <p ref={ref} className={cn('text-sm text-gray-700', className)} {...props}>
        Showing <span className="font-medium">{start}</span> to{' '}
        <span className="font-medium">{end}</span> of{' '}
        <span className="font-medium">{totalItems}</span> results
      </p>
    );
  }
);

PaginationInfo.displayName = 'PaginationInfo';
